import type { QueryClient, QueryKey, Updater } from '@tanstack/react-query';
import { hashKey } from '@tanstack/react-query';

import { reReadPatchedQuery } from '~hooks/useMessaging/reReadPatchedQuery';

type PendingReRead = { isAnotherRequested: boolean };

const pendingReReadsByClient = new WeakMap<QueryClient, Map<string, PendingReRead>>();

function pendingReReadsFor(queryClient: QueryClient) {
  const existing = pendingReReadsByClient.get(queryClient);
  if (existing) return existing;

  const pendingReReads = new Map<string, PendingReRead>();
  pendingReReadsByClient.set(queryClient, pendingReReads);

  return pendingReReads;
}

export const isListBeingRead = (queryClient: QueryClient, listKey: QueryKey) =>
  queryClient.getQueryState(listKey)?.fetchStatus === 'fetching';

/**
 * One re-read per list at a time: a burst of messages during a read would otherwise restart it
 * once per message. A message landing mid-read asks for one more read after it instead.
 */
export async function reReadPatchedList(queryClient: QueryClient, listKey: QueryKey) {
  const pendingReReads = pendingReReadsFor(queryClient);
  const listHash = hashKey(listKey);
  const pendingReRead = pendingReReads.get(listHash);
  if (pendingReRead) {
    pendingReRead.isAnotherRequested = true;
    return;
  }

  const reRead: PendingReRead = { isAnotherRequested: false };
  pendingReReads.set(listHash, reRead);

  try {
    do {
      reRead.isAnotherRequested = false;
      const listFetchState = queryClient.getQueryState(listKey);
      const isBeingRead = isListBeingRead(queryClient, listKey);
      const isFirstRead = listFetchState?.data === undefined;
      const isReadingNextPage = Boolean(listFetchState?.fetchMeta?.fetchMore);
      const shouldWaitForRead = isBeingRead && (isFirstRead || isReadingNextPage);

      await reReadPatchedQuery(
        queryClient,
        { queryKey: listKey, exact: true },
        shouldWaitForRead,
      );
    } while (reRead.isAnotherRequested);
  } finally {
    pendingReReads.delete(listHash);
  }
}

/** A read on its way would land the list as it was before this write. */
export function writePatchedList<TList>(
  queryClient: QueryClient,
  listKey: QueryKey,
  patch: Updater<TList | undefined, TList | undefined>,
) {
  queryClient.setQueryData<TList>(listKey, patch);

  if (isListBeingRead(queryClient, listKey)) {
    void reReadPatchedList(queryClient, listKey);
  }
}
